
import { Component, Emit, Model, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { ClipsEvent } from './types';

import TitleBackdropsWrapper from '@/components/details/TitleBackdropsWrapper.vue';
import DisneyPlusPromo from '@/components/DisneyPlusPromo.vue';
import { getDisneyPromoText, DisneyPlusId } from '@/helpers/disneyPlusPromo';

import type { WebLocale } from '@/enums/web-locale';
import { getBackdropClips } from '@/helpers/title-helper';

const ImpressionPixel = () =>
	import(/* webpackChunkName: "impression-pixel" */ '@/components/sponsored-recommendations/ImpressionPixel.vue');

const BackdropCarousel = () =>
	import(/* webpackChunkName: "backdrop-carousel" */ '@/components/details/backdrops/BackdropCarousel.vue');
const BackdropTrailer = () =>
	import(/* webpackChunkName: "backdrop-trailer" */ '@/components/details/backdrops/BackdropTrailer.vue');

const Language = namespace('language');
const Routing = namespace('routing');
const User = namespace('user');

// APPLE_BACKDROP_EXP_V2
import AppleBackdropExpV2, {
	AppleBackdropExpV2Name,
	AppleBackdropExpV2Variants,
} from '@/components/experiments/AppleBackdropExpV2';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { appleBackdropV2AffiliateLinksParams } from '@/components/experiments/AppleBackdropExpV2/affiliateParams';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
const Experiment = namespace('experiment');
// APPLE_BACKDROP_EXP_V2

@Component({
	name: 'TitleBackdrops',
	components: {
		TitleBackdropsWrapper,
		BackdropCarousel,
		BackdropTrailer,
		DisneyPlusPromo,
		ImpressionPixel,
	},
})
export default class TitleBackdrops extends Vue {
	@Prop({ required: true }) title: TitleDetail;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];
	@Prop({ required: true }) isAVODActive: boolean;
	@Prop({ default: false }) hasDisneyOffer: boolean;

	// APPLE_BACKDROP_EXP_V2
	@Experiment.Getter activeVariantsWithControlGroup: Record<string, string>;
	@Prop() appleBackdropV2ExpActive: boolean;
	// APPLE_BACKDROP_EXP_V2

	@Model(ClipsEvent.MODEL, { default: null }) readonly activeClipId!: string | null;

	@Language.State webLocale: WebLocale;
	@Language.Getter country: string;
	@Routing.State activeRoute: any;

	@User.Getter isLoggedIn: () => boolean;
	@User.Getter isPremium: boolean;

	get isDisneyPromotion() {
		return (
			getDisneyPromoText(this.country) !== null &&
			this.title.offers.some(offer => offer.package.packageId === DisneyPlusId)
		);
	}

	get titleName() {
		return this.title.content.title;
	}

	get titleClips() {
		// START AVOD
		const PlexClip = this.isAVODActive
			? [
					{
						provider: 'PLEX',
						externalId: this.title.plexPlayerOffers[0].id,
						sourceUrl: this.title.plexPlayerOffers[0].standardWebURL,
					},
			  ]
			: null;
		// END AVOD
		return PlexClip || getBackdropClips(this.title);
	}

	get hasBackdropContent(): boolean {
		const hasCarouselImages = this.title.content.backdrops.length > 0;
		const hasTrailerClips = this.titleClips.length > 0;

		return hasCarouselImages || hasTrailerClips;
	}

	get backdropClasses() {
		return {
			backdrops__carousel: !this.shouldShowTrailerBackdrop && this.shouldShowCarouselBackdrop,
			'backdrops--trailer-playing': !!this.activeClipId,
		};
	}

	get shouldShowTrailerBackdrop(): boolean {
		// START AVOD
		if (this.isAVODActive) {
			return !this.isPremium;
		}
		// END AVOD

		return !this.isLoggedIn() && !!this.title.content.title && this.titleClips.length > 0;
	}

	get shouldShowCarouselBackdrop(): boolean {
		return this.imageUrls.length > 0;
	}

	get imageUrls(): String[] {
		const pathExtension = this.title.content.fullPath
			? `/${this.title.content.fullPath.substring(this.title.content.fullPath.lastIndexOf('/') + 1)}`
			: '';

		return this.title.content.backdrops.map(backdrop => backdrop.backdropUrl + pathExtension);
	}

	mounted() {
		if (this.activeClipId) {
			this.emitTrailerPlayingModel(null);
		}
	}

	// APPLE_BACKDROP_EXP_V2

	get appleBackdropV2ShowPlayButton() {
		if (!this.appleBackdropV2ExpActive) return false;

		return this.activeVariantsWithControlGroup[AppleBackdropExpV2Name] === AppleBackdropExpV2Variants.VARIANT_1;
	}

	get observeVisibilityOptions() {
		return {
			...observableOptions,
			once: true,
			callback: (isVisible: boolean) => this.onVisibilityChanged(isVisible),
		};
	}

	onVisibilityChanged(isVisible: boolean) {
		if (
			isVisible &&
			this.appleBackdropV2ExpActive &&
			//only when play button visible
			(this.shouldShowTrailerBackdrop || this.appleBackdropV2ShowPlayButton)
		) {
			AppleBackdropExpV2.trackEvent({
				action: 'impression',
				label: 'play_button',
				property: 'buybox',
			});
		}
	}

	appleBackdropV2Url(packageId: number) {
		const offer = (this.title.appleOffers ? this.title.appleOffers : []).find(
			offer => offer.package.packageId === packageId
		);

		const url = new ClickoutUrl(offer);

		url.set('uct_ct', appleBackdropV2AffiliateLinksParams['variant_1'].play?.ct).set(
			'uct_itsct',
			appleBackdropV2AffiliateLinksParams['variant_1'].play?.itsct
		);

		return url.toString();
	}

	handleAppleBackdropV2Variants() {
		const url = this.title.appleOffers.some(offer => offer.package.packageId == 350)
			? this.appleBackdropV2Url(350)
			: this.appleBackdropV2Url(2);
		window.open(url, '_blank');
	}
	// APPLE_BACKDROP_EXP_V2

	@Watch('activeRoute')
	onRouteChange() {
		this.emitTrailerPlayingModel(null);
	}

	// APPLE_BACKDROP_EXP_V2
	appleBackdropV2PlayCalled = false;
	// APPLE_BACKDROP_EXP_V2

	handleBackdropTrailer(activeClipId: string | null) {
		// APPLE_BACKDROP_EXP_V2

		if (this.appleBackdropV2ExpActive) {
			if (!this.appleBackdropV2PlayCalled) {
				AppleBackdropExpV2.trackEvent({ action: 'click', label: 'play_button', property: 'buybox' });
				this.appleBackdropV2PlayCalled = true;
			}
			if (
				this.activeVariantsWithControlGroup[AppleBackdropExpV2Name] !== AppleBackdropExpV2Variants.CONTROL &&
				this.appleBackdropV2ShowPlayButton
			) {
				this.handleAppleBackdropV2Variants();
				return;
			}
		}

		// APPLE_BACKDROP_EXP_V2

		this.emitTrailerPlayingModel(activeClipId);
		this.emitBackdropClicked();
	}

	@Emit(ClipsEvent.MODEL)
	emitTrailerPlayingModel(activeClipId: string | null) {
		return activeClipId;
	}

	@Emit('backdrop-clicked')
	emitBackdropClicked() {}
}
